import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { OptionItemState } from '@deltek/specification-client/core/Models/textContentElement'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { BestPracticesIcons } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesIcons'
import Button from '@material-ui/core/Button'
import { darken, makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { MoreVert } from '@material-ui/icons'

import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    contentTeamProjectId,
    isContentTeam,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import clsx from 'clsx'
import { TrackChangeType } from 'models/changes'
import {
    manufacturerColor,
    manufacturerHoverColor,
} from 'pages/ProjectsPage/colorVars'
import React, { useMemo } from 'react'
import { AreaContext } from '../Common/AreaContext'
import DeletePills from './DeletePills'

const useStyles = makeStyles((theme: Theme) => ({
    optionItem: {
        border: '1px solid #F78909',
        padding: theme.spacing(0.5, 1),
        lineHeight: pxToRem(15),
        borderRadius: pxToRem(70),
        wordBreak: 'break-word',
        '&:disabled': {
            color: '#8e8f91',
            border: (props: PropertyEditorOptionItemStyleProps) =>
                props.isElementDeleted
                    ? '1px solid #FF6060'
                    : '1px solid #8e8f91',
            background: theme.palette.common.white,
            padding: theme.spacing(0.5, 1),
        },
    },
    additionalOptionItem: {
        border: `1px solid ${manufacturerColor}`,
        color: manufacturerColor,
        margin: (props: PropertyEditorOptionItemStyleProps) =>
            props.calledFromImportPage ? theme.spacing(0.5, 0.25) : 'inherit',
    },
    iconButtonStyle: {
        padding: 0,
        paddingLeft: 5,
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    deletedElementText: {
        color: '#8e8f91',
        border: '1px solid #FF6060',
    },
    addedElementText: {
        color: '#FF6060',
        border: '1px solid #FF6060',
    },
    inActiveSelectedOptionItem: {
        color: '#d2d2d2',
        border: '1px solid #d2d2d2',
        background: theme.palette.common.white,
        margin: (props: PropertyEditorOptionItemStyleProps) =>
            props.calledFromImportPage ? theme.spacing(0.5, 0.25) : 'inherit',
    },
    selectedAdditionalOptionItem: {
        padding: theme.spacing(0.25, 1),
        background: manufacturerColor,
        border: `1px solid ${manufacturerColor}`,
        color: theme.palette.common.white,
        '&:hover': {
            background: manufacturerHoverColor,
        },
    },
    selectedOptionItem: {
        background: '#F78909',
        padding: theme.spacing(0.5, 2.2),
        color: theme.palette.common.white,
        '&:hover': {
            background: darken('#F78909', 0.05),
        },
        '&:disabled': {
            color: theme.palette.common.white,
            background: '#40464f',
            border: '1px solid #878d96',
        },
    },
    unselectedOptionItem: {
        color: '#F78909',
        background: '#FFF9F2',
        margin: (props: PropertyEditorOptionItemStyleProps) =>
            props.calledFromImportPage ? theme.spacing(0.5, 0.25) : 'inherit',
    },

    endIcon: {
        color: (props: PropertyEditorOptionItemStyleProps) =>
            props.selected
                ? props.disabled
                    ? '#10B091'
                    : theme.palette.common.white
                : '#10B091',
        fontSize: pxToRem(20),
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0),
        '&:disabled': {
            color: theme.palette.common.white,
        },
        '& > .bpIconContainer': {
            fontSize: pxToRem(17),
            height: pxToRem(16),
        },
    },
    activeBPIcon: {
        color: theme.palette.common.white,
        cursor: 'default',
    },
    inActiveBPIcon: {
        color: '#10B091',
        cursor: 'default',
    },
    strikeout: {
        position: 'absolute',
        border: '1px solid #FF6060',
        borderBottom: 0,
        marginTop: `-${pxToRem(2.1)}`,
        left: `-${pxToRem(5)}`, // This is needed to be negative so that the strikeout fall over the element
        cursor: 'pointer',
        userSelect: 'none',
    },
    buttonLabel: {
        margin: theme.spacing(0, 2),
    },
    buttonLabelInactive: {
        margin: theme.spacing(0.5, 2.5),
    },
    bpOptContainer: {
        color: theme.palette.common.white,
        position: 'relative',
        right: pxToRem(3),
        fontSize: pxToRem(17),
        '& > svg': {
            color: 'inherit',
        },
    },
}))

interface PropertyEditorOptionItemStyleProps {
    calledFromImportPage?: boolean
    selected?: boolean
    disabled?: boolean
    isElementDeleted?: boolean
}
export interface PropertyEditorOptionItem
    extends PropertyEditorOptionItemStyleProps {
    value?: string
    onClick?: (currentlySelected: boolean) => void
    onRightClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        elementId: string | undefined,
        isBestPractice: boolean,
        isGlobalBestPractice: boolean
    ) => void
    elementId?: string
    isBestPractice?: boolean
    isGlobalBestPractice?: boolean
    isPremiumPlus?: boolean
    state?: OptionItemState | null
    selected?: boolean
    disabled?: boolean
    onDeleteOption?: () => void
    contentTeamFromStudioPage?: boolean
    isManufacturerContentPills?: boolean
    isDeleteIconVisible?: boolean
    changeType?: TrackChangeType
    fieldStateType?: string
    projectId?: string
    isBPMenuOpen?: boolean
    setIsBPMenuOpen?: React.Dispatch<React.SetStateAction<boolean>>
    mouseOverElement?: string | null
    setMouseOverElement?: React.Dispatch<React.SetStateAction<string | null>>
    isBestPracticeEditDisabled?: boolean
    isBestPracticeViewDisabled?: boolean
    hoverText?: string[]
}

const PropertyEditorOptionItem: React.FC<PropertyEditorOptionItem> = ({
    value = '',
    onClick,
    onDeleteOption,
    onRightClick,
    elementId,
    isBestPractice = false,
    isGlobalBestPractice = false,
    isPremiumPlus = false,
    state,
    disabled = false,
    isBestPracticeEditDisabled = false,
    isBestPracticeViewDisabled = false,
    calledFromImportPage,
    contentTeamFromStudioPage,
    isManufacturerContentPills,
    isDeleteIconVisible,
    changeType,
    fieldStateType,
    isBPMenuOpen,
    setIsBPMenuOpen,
    mouseOverElement,
    setMouseOverElement,
    projectId,
    hoverText,
    ...rest
}) => {
    const { selectedProjectNode } = React.useContext(AreaContext)

    const selected = React.useMemo<boolean>(() => state === 'OptedIn', [state])
    const pillRef = React.useRef<HTMLButtonElement | null>(null)
    const { isTrackChangeToggleOn: isTrackChangeToggleOn } = useReactiveVar(
        showHideToggles
    )

    const { isSystemUser, isAdmin, isPublishingAuthor } = useReactiveVar(
        userInfo
    )
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const contentTeamProject = useReactiveVar(contentTeamProjectId)
    const isStudioManagement = React.useMemo(
        () =>
            isContentTeamUser &&
            projectId === contentTeamProject &&
            isSystemUser &&
            (isPublishingAuthor || isAdmin),
        [isContentTeamUser, projectId, contentTeamProject]
    )
    const isInactive = useMemo(() => state === 'InActive', [state])

    const isSelectedOptionItem = React.useMemo(
        () => !!selected && !isPremiumPlus && !isInactive,
        [selected, isPremiumPlus, isInactive]
    )

    const handleClick = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            const el = event.target as HTMLInputElement
            if (
                el.getAttribute('data-testid') === 'moreMenu' ||
                el.parentElement?.getAttribute('data-testid') === 'moreMenu'
            ) {
                return
            }
            if (!disabled) {
                onClick?.(selected)
                setMouseOverElement?.(null) // removing kabob to disable BP tagging
            }
        },
        [selected, onClick, disabled, mouseOverElement]
    )

    const handleRightClick = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.preventDefault()
            if (
                isBestPracticeEditDisabled ||
                disabled ||
                !isSelectedOptionItem || // added condition to enable BP tagging only on selected pills
                !elementId
            )
                return
            if (event.type === 'contextmenu') {
                onRightClick?.(
                    event,
                    elementId,
                    isBestPractice,
                    isGlobalBestPractice
                )
            }
        },
        [
            onRightClick,
            disabled,
            elementId,
            isBestPractice,
            isGlobalBestPractice,
            isBestPracticeEditDisabled,
            isSelectedOptionItem,
        ]
    )

    // fn to display BP icon button on mouse enter on pill
    const handleMouseEnter = React.useCallback(() => {
        // added condition to enable BP tagging only on selected pills
        if (isBPMenuOpen || !isSelectedOptionItem) return
        setMouseOverElement?.(elementId!)
    }, [mouseOverElement, isBPMenuOpen, elementId, isSelectedOptionItem])

    // fn to hide BP icon button on mouse leave on pill
    const handleMouseLeave = React.useCallback(() => {
        // added condition to enable BP tagging only on selected pills
        if (isBPMenuOpen || !isSelectedOptionItem) return
        setMouseOverElement?.(null)
    }, [mouseOverElement, isBPMenuOpen, elementId, isSelectedOptionItem])

    const handleBPMenuDisplay = React.useCallback(
        (
            event: React.MouseEvent<
                HTMLDivElement | HTMLButtonElement,
                MouseEvent
            >
        ) => {
            setIsBPMenuOpen?.(true)
            onRightClick?.(
                event as React.MouseEvent<HTMLButtonElement, MouseEvent>,
                elementId,
                isBestPractice,
                isGlobalBestPractice
            )
        },
        [elementId, isBestPractice, isGlobalBestPractice, mouseOverElement]
    )

    const isRemoveIconVisible = React.useMemo(() => {
        return (
            (selectedProjectNode?.elementType !== 'ASSEMBLY' &&
                contentTeamFromStudioPage &&
                !isManufacturerContentPills) ||
            (isDeleteIconVisible && contentTeamFromStudioPage)
        )
    }, [
        selectedProjectNode?.elementType,
        isDeleteIconVisible,
        contentTeamFromStudioPage,
        isManufacturerContentPills,
    ])
    const showRemoveIconOnSMM = React.useMemo(() => {
        return isManufacturerContentPills ? !isStudioManagement : true
    }, [isManufacturerContentPills, isStudioManagement])
    const isElementAdded = React.useMemo(
        () =>
            (!!changeType &&
                changeType.toLowerCase() ===
                    TrackChangeType.ADDED.toLowerCase()) ??
            false,
        [changeType]
    )

    const isElementDeleted = React.useMemo(
        () =>
            (!!changeType &&
                changeType.toLowerCase() ===
                    TrackChangeType.DELETED.toLowerCase()) ??
            false,
        [changeType]
    )

    const classes = useStyles({
        calledFromImportPage,
        selected,
        disabled,
        isElementDeleted,
    })

    const strikeOut = React.useCallback(() => {
        if (pillRef.current) {
            const lineHeight = parseInt(
                window
                    .getComputedStyle(pillRef.current, null)
                    .getPropertyValue('line-height')
            )
            const elemHeight = pillRef.current.clientHeight
            const lines = Math.floor(elemHeight / lineHeight) // using calculated lineheight to detect line breaks
            const width = Number(pillRef.current?.clientWidth) + 10
            return { width, lines }
        }
    }, [pillRef, isDeleteIconVisible, isElementDeleted])

    const renderStrikeout = React.useCallback(() => {
        return (
            !!strikeOut()?.lines &&
            Array(strikeOut()?.lines)
                .fill('')
                .map((_, index) => (
                    <span
                        key={index}
                        className={classes.strikeout}
                        style={{
                            width: `${strikeOut()?.width}px`,
                            top: pxToRem((index + 1) * 12.5 + 3 * index),
                        }}
                    />
                ))
        )
    }, [strikeOut, isDeleteIconVisible, isElementDeleted])

    const currentColor = React.useMemo(() => {
        switch (true) {
            case disabled && !selected:
                return '#8E8F91'
            case selected:
                return '#FFFFFF'
            case isTrackChangeToggleOn && isElementAdded:
                return '#FF6060'
            case isInactive:
                return '#D2D2D2'
            default:
                return '#F78909'
        }
    }, [disabled, selected, isTrackChangeToggleOn, isElementAdded, isInactive])

    const getButtonClassNames = React.useMemo(() => {
        const outOfBoundsChangeType = [
            'disabled',
            'optiongroup',
            'resetoption',
        ].includes(changeType?.toLowerCase() ?? '')

        const isDeletedFromSMM = contentTeamFromStudioPage && isInactive
        const isSelectedAdditionalOptionItem =
            !!isPremiumPlus && !!selected && !isInactive
        const isUnselectedOptionItem =
            (!selected && !isPremiumPlus && !isInactive && !isElementAdded) ||
            (isDeletedFromSMM && outOfBoundsChangeType)
        const isAdditionalOptionItem =
            !!isPremiumPlus && !selected && !isInactive
        const isInActiveSelectedOption =
            (!contentTeamFromStudioPage && isInactive) ||
            (isDeletedFromSMM && !outOfBoundsChangeType)

        return clsx('optionItem', classes.optionItem, {
            [classes.selectedOptionItem]: isSelectedOptionItem,
            [classes.selectedAdditionalOptionItem]: isSelectedAdditionalOptionItem,
            [classes.unselectedOptionItem]: isUnselectedOptionItem,
            [classes.additionalOptionItem]: isAdditionalOptionItem,
            [classes.inActiveSelectedOptionItem]: isInActiveSelectedOption,
            [classes.addedElementText]: isTrackChangeToggleOn && isElementAdded,
            [classes.deletedElementText]:
                isTrackChangeToggleOn && isElementDeleted,
        })
    }, [
        selected,
        isPremiumPlus,
        isInactive,
        isElementAdded,
        isElementDeleted,
        isTrackChangeToggleOn,
        changeType,
        contentTeamFromStudioPage,
    ])

    return (
        <React.Fragment>
            <HoverTooltip
                text={
                    hoverText?.length
                        ? hoverText.map((line) => (
                              <Typography display="block">{line}</Typography>
                          ))
                        : ''
                }
                placements={['bottom']}
            >
                <Button
                    {...rest}
                    ref={pillRef}
                    onClick={handleClick}
                    onContextMenu={handleRightClick}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    disabled={disabled}
                    className={getButtonClassNames}
                    classes={{
                        endIcon: classes.endIcon,
                    }}
                    endIcon={
                        <>
                            {isTrackChangeToggleOn &&
                                isElementDeleted &&
                                renderStrikeout()}
                            {isRemoveIconVisible &&
                                !isElementDeleted &&
                                showRemoveIconOnSMM && (
                                    <DeletePills
                                        isInsertText={false}
                                        onDeleteOption={onDeleteOption}
                                        htmlColor={currentColor}
                                    />
                                )}
                            {!isBestPracticeViewDisabled && (
                                <GridItem
                                    className={clsx('bpIconContainer', {
                                        [classes.activeBPIcon]: selected,
                                        [classes.inActiveBPIcon]: !selected,
                                    })}
                                >
                                    <BestPracticesIcons
                                        isBestPractice={isBestPractice}
                                        isGlobalBestPractice={
                                            isGlobalBestPractice
                                        }
                                    />
                                </GridItem>
                            )}
                            {!isBestPracticeEditDisabled &&
                                isSelectedOptionItem &&
                                mouseOverElement === elementId && (
                                    <GridItem
                                        onClick={handleBPMenuDisplay}
                                        className={classes.bpOptContainer}
                                    >
                                        <MoreVert
                                            fontSize="inherit"
                                            data-testid="moreMenu"
                                            style={{ position: 'absolute' }}
                                        />
                                    </GridItem>
                                )}
                        </>
                    }
                >
                    <span
                        className={clsx({
                            [classes.buttonLabel]:
                                fieldStateType === 'ReplacementOption',
                            [classes.buttonLabelInactive]:
                                fieldStateType === 'ReplacementOption' &&
                                isInactive &&
                                value.length > 400,
                        })}
                    >
                        {value}
                    </span>
                </Button>
            </HoverTooltip>
        </React.Fragment>
    )
}

export default PropertyEditorOptionItem
